import * as XLSX from 'xlsx';
import type { FlatRow, KeyId } from '../types.local';
import { keyOf } from '../types.local';
import { toProjectDateOnly } from 'src/utils/date';

function calcInclusiveDays(start?: string | null, finish?: string | null): number | null {
    if (!start || !finish) return null;

    const s = new Date(`${start}T00:00:00`);
    const f = new Date(`${finish}T00:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(f.getTime())) return null;

    const ms = f.getTime() - s.getTime();
    const days = Math.floor(ms / 86400000) + 1;
    return days >= 1 ? days : null;
}

function toExportDateOnly(v?: string | null, zone = 'Asia/Seoul'): string {
    if (!v) return '';

    const s = String(v).trim();
    if (!s) return '';

    // 이미 화면용 DateOnly라면 그대로 사용
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

    // UTC ISO 등 시점 문자열이면 프로젝트/로컬 날짜로 변환
    return toProjectDateOnly(s, zone) || '';
}

function makeFileName(prefix = 'wbs_export') {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const mi = String(now.getMinutes()).padStart(2, '0');
    return `${prefix}_${yyyy}${mm}${dd}_${hh}${mi}.xlsx`;
}

export function exportWbsRowsToExcel(args: {
    rows: FlatRow[];
    previewOn?: boolean;
    previewCodeMap?: Record<KeyId, string>;
    fileName?: string;
    zone?: string;
}) {
    const { rows, previewOn = false, previewCodeMap = {}, fileName, zone = 'Asia/Seoul' } = args;

    const exportRows = rows
        .filter((r) => !r.isDeleted)
        .map((r) => {
            const id = keyOf(r);
            const code = (previewOn ? previewCodeMap[id] : undefined) ?? r.code ?? previewCodeMap[id] ?? '';

            const start = toExportDateOnly(r.plannedStart, zone);
            const finish = toExportDateOnly(r.plannedFinish, zone);

            return {
                Code: code,
                'Wbs Name': r.name ?? '',
                Weight: r.weight == null || r.weight === '' ? '' : String(r.weight),
                Start: start,
                Finish: finish,
                Days: calcInclusiveDays(start || null, finish || null) ?? r.durationDays ?? '',
            };
        });

    const ws = XLSX.utils.json_to_sheet(exportRows);

    ws['!cols'] = [
        { wch: 14 }, // Code
        { wch: 40 }, // Wbs Name
        { wch: 12 }, // Weight
        { wch: 14 }, // Start
        { wch: 14 }, // Finish
        { wch: 10 }, // Days
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WBS');

    XLSX.writeFile(wb, fileName ?? makeFileName('wbs_export'));
}
